/* ════════════ THE HARVEST ════════════
   The facebook page's atmosphere: a faint social graph. dots, short
   edges, friendship. periodically a cluster lights up periwinkle and
   the edges retract into a single node, which brightens, then fades.
   connection as extraction. it was in the terms. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

function harvest(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#socialgraph")!;
  const ctx = c.getContext("2d")!;

  const PERI = "#7b96ff";
  const GREY = "#9aa0a6";

  type Cluster = {
    cx: number;
    cy: number;
    t: number; // age in frames
    life: number;
    hot: boolean; // hot clusters get harvested
    pts: { ox: number; oy: number }[]; // friends, relative to the hub
  };
  let clusters: Cluster[] = [];

  const spawn = (): Cluster => {
    const hot = Math.random() < 0.18;
    const n = 4 + Math.floor(Math.random() * 4);
    const r = 26 + Math.random() * 34;
    return {
      cx: Math.random() * c.width,
      cy: Math.random() * c.height,
      t: 0,
      life: 380 + Math.random() * 340,
      hot,
      pts: Array.from({ length: n }, (_, i) => {
        const a = (i / n) * Math.PI * 2 + Math.random() * 0.9;
        const d = r * (0.55 + Math.random() * 0.45);
        return { ox: Math.cos(a) * d, oy: Math.sin(a) * d };
      }),
    };
  };

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    const count = Math.min(16, Math.floor((c.width * c.height) / 90000));
    clusters = Array.from({ length: count }, () => {
      const g = spawn();
      g.t = Math.random() * g.life; // desync
      return g;
    });
  };
  seed();
  addEventListener("resize", seed, { signal });

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    for (const g of clusters) {
      const p = g.t / g.life;
      const a = p < 0.12 ? p / 0.12 : p > 0.88 ? (1 - p) / 0.12 : 1;
      // the harvest: hot clusters retract their edges into the hub at p 0.5→0.8
      const q = g.hot ? (p < 0.5 ? 0 : p < 0.8 ? (p - 0.5) / 0.3 : 1) : 0;
      const pull = q * q; // ease in, the way consent erodes
      const col = g.hot ? PERI : GREY;
      const base = g.hot ? 0.45 : 0.18;
      ctx.strokeStyle = col;
      ctx.fillStyle = col;
      ctx.lineWidth = 1;
      for (const pt of g.pts) {
        const x = g.cx + pt.ox * (1 - pull);
        const y = g.cy + pt.oy * (1 - pull);
        ctx.globalAlpha = a * base * (1 - pull * 0.7);
        ctx.beginPath();
        ctx.moveTo(g.cx, g.cy);
        ctx.lineTo(x, y);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(x, y, 1.4, 0, Math.PI * 2);
        ctx.fill();
      }
      // the hub: brightens as it absorbs the cluster, then fades with the rest
      ctx.globalAlpha = a * (base + pull * 0.4);
      ctx.beginPath();
      ctx.arc(g.cx, g.cy, 1.6 + pull * 1.8, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    for (let i = 0; i < clusters.length; i++) {
      const g = clusters[i]!;
      g.t++;
      if (g.t >= g.life) clusters[i] = spawn();
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

onPage(harvest);
